/**
 * DAŚĀ ERA SEGMENTS — the fourth evidence track.
 *
 * The mahadasha (life-period) timeline is the SAME for all 12 rising signs (it comes from the Moon,
 * not the lagna), but its boundaries shift with the exact birth TIME: a Jupiter period that runs
 * 2000–2007 for a noon birth might run 2001–2008 for a 1 pm birth. So across the birth-time search
 * range, each period boundary SWEEPS across a band of calendar dates. Those sweep-bands are the only
 * places where knowing "what your life was about then" actually distinguishes one candidate birth
 * time from another — and therefore helps narrow the rising sign.
 *
 * This module scans the WHOLE search range, builds each candidate's mahadasha timeline, and splits
 * the person's life into:
 *   • STABLE eras   — spans where every candidate agrees which period was running (ask for context,
 *                     but they don't discriminate), and
 *   • TRANSITION zones — the boundary sweep-bands where candidates DISAGREE (lord A vs lord B). These
 *                     are the discriminating questions: "around <zone>, did life feel like <A> or <B>?"
 *
 * Boundary dates are MONOTONIC in birth time (more Moon traversed → boundaries earlier), so at a date
 * inside a transition zone the EARLIER-born candidates are still in lord A while the LATER-born are
 * already in lord B — which is exactly the signal a user's answer resolves.
 *
 * Pure / deterministic. The caller supplies `now` (or a horizon) to focus on already-lived eras.
 */
import { calculate_chart, local_to_jd } from "../astro/chart";
import { get_config } from "../astro/config";
import { init_dasha_jd } from "../astro/dasha";
import { DASHA_SEQUENCE, DASHA_DAYS, SIDEREAL_YEAR_DAYS } from "../astro/constants";
import { pymod } from "./pyutil";
import { addSeconds, diffDays, parseISODate, plainDateTime } from "./datetime";
import { birth_offset_at } from "./timezones";

/** "HH:MM[:SS]" → seconds of day. */
function _label_seconds(label: any): number {
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(String(label ?? ""));
  if (!m) return 0;
  return +m[1] * 3600 + +m[2] * 60 + (+(m[3] ?? 0));
}
/**
 * [start, end] seconds measured from the birth day's midnight. DATE-AWARE, mirroring
 * scoring.search_span_seconds: when the window carries explicit calendar bounds
 * (start_date/end_date) the true gap is spanned, so a multi-day (induced-delivery) window keeps
 * its real >24h width instead of collapsing to the clock-label difference (which reported zero
 * width and hence no dasha transition zones). A same-day pair and the plain overnight case
 * (end < start rolls +24h) reduce to exactly the old time-of-day math.
 */
export function _window_seconds(search: Record<string, any>): [number, number] {
  const start = _label_seconds(search["start"]);
  const end_raw = _label_seconds(search["end"]);
  if (search["start_date"] && search["end_date"]) {
    try {
      const days = diffDays(parseISODate(String(search["end_date"])), parseISODate(String(search["start_date"])));
      const total = days * 86400 + (end_raw - start);
      // Defensive: an inconsistent date pair (e.g. an overnight window whose end date was lost)
      // would clamp to zero width — fall through to the circular time-of-day math instead.
      if (total > 0) return [start, start + total];
    } catch {
      // unparseable dates → fall through to the time-of-day math
    }
  }
  let end = end_raw;
  if (end < start) end += 24 * 3600;
  return [start, end];
}

const _ORDER: string[] = DASHA_SEQUENCE.map(([lord]) => lord);

// The life-themes a user can pick for an era / discriminating window, each mapped to an existing
// event category (so era answers score through the SAME tested significator machinery as events).
export const ERA_THEMES: Array<{ key: string; label: string; category: string }> = [
  { key: "career", label: "Career / work / status", category: "career_breakthrough" },
  { key: "marriage", label: "Marriage / partner / love", category: "marriage" },
  { key: "children", label: "Children / parenthood", category: "child_birth" },
  { key: "home", label: "Home / property / family life", category: "property_purchase" },
  { key: "money", label: "Money / gains / success", category: "social_celebration" },
  { key: "study", label: "Study / learning / new skill", category: "education_start" },
  { key: "health", label: "Health / hardship / struggle", category: "anxiety_health" },
  { key: "loss", label: "Loss / endings / grief", category: "death_family" },
  { key: "travel", label: "Travel / move / big change", category: "travel_family" },
];
const _THEME_CATEGORY: Record<string, string> = Object.fromEntries(ERA_THEMES.map((t) => [t.key, t.category]));

/** "YYYY-MM" → absolute month index, or null if unparseable. */
function _ym_index(ym: string): number | null {
  const m = /^(\d{4})-(\d{2})/.exec(String(ym));
  if (!m) return null;
  return +m[1] * 12 + (+m[2] - 1);
}

/** Absolute month index → "YYYY-MM-15" (mid-month date, same convention as the old midpoint). */
function _ym_index_to_date(idx: number): string {
  const year = Math.floor(idx / 12);
  const month = (idx % 12) + 1;
  return `${year}-${String(month).padStart(2, "0")}-15`;
}

// Mean Gregorian month length, used to convert a phase's month span into days.
const _AVG_MONTH_DAYS = 30.44;
// Phases at/below this many months keep the single midpoint sample; longer phases are sampled at
// their quartiles (a multi-year phase's dasha signal is NOT constant across it — one midpoint
// sample was midpoint-sampling luck).
const _ERA_SINGLE_SAMPLE_MAX_MONTHS = 12;

/**
 * Convert the user's saved era answers into pseudo-events, sampled ACROSS each answered life-phase.
 * A single era can hold SEVERAL answers (over many years more than one thing happens),
 * each tagged with a VALENCE (`positive` = the area thrived / `negative` = it was a hard stretch): a
 * negative answer scores against the area's CONTRA houses (see `event_house_set`), so "good marriage"
 * and "hard marriage" in the same period point at DIFFERENT rising signs — doubling the signal.
 *
 * SAMPLING: a short phase (≤ ~1 year) emits ONE midpoint pseudo-event exactly as before. A longer
 * phase emits THREE samples at its 25% / 50% / 75% points — a multi-year phase's running dasha is
 * not constant across it, so a single fixed midpoint was midpoint-sampling luck. Each sample's
 * `date_accuracy_days` widens with the phase (max(120, span/4)) and its `weight` is the single
 * event's weight SPLIT across the samples (1/3 each), so the answer's TOTAL influence in the
 * weighted-mean event scoring is unchanged.
 *
 * `user_profile.era_answers`: [{start, end, theme, valence?, influence?}]. The event scorer reads the
 * running dasha at each sample date for each candidate birth time: because a discriminating window's
 * dasha lord changes with the birth time, these pseudo-events carry the era signal straight into the
 * normal events ranking. A no-op for anyone who hasn't answered any eras.
 */
export function era_pseudo_events(user_profile: Record<string, any>): Array<Record<string, any>> {
  const answers = Array.isArray(user_profile["era_answers"]) ? user_profile["era_answers"] : [];
  const out: Array<Record<string, any>> = [];
  for (const a of answers) {
    const cat = _THEME_CATEGORY[String(a?.["theme"] ?? "")];
    if (!cat || !a?.["start"] || !a?.["end"]) continue;
    const m0 = _ym_index(a["start"]);
    const m1 = _ym_index(a["end"]);
    if (m0 === null || m1 === null) continue;
    const span_months = Math.max(0, m1 - m0);
    const span_days = span_months * _AVG_MONTH_DAYS;
    // Quartile sampling for long phases; the lone 0.5 fraction reproduces the old midpoint
    // (Math.round((m0+m1)/2)) byte-for-byte for short phases.
    const fractions = span_months <= _ERA_SINGLE_SAMPLE_MAX_MONTHS ? [0.5] : [0.25, 0.5, 0.75];
    const sample_weight = 1 / fractions.length; // split so the TOTAL era influence per answer is unchanged
    const date_accuracy = Math.max(120, span_days / 4);
    const negative = String(a?.["valence"] ?? "positive").toLowerCase() === "negative";
    for (const f of fractions) {
      const at = _ym_index_to_date(Math.round(m0 + span_months * f));
      out.push({
        start_date: at, end_date: at, category: cat,
        label: `Life phase: ${a["theme"]} (${negative ? "hard" : "good"})`,
        valence: negative ? "negative" : "positive",
        confidence: 1, weight: sample_weight, date_accuracy_days: date_accuracy, has_exact_time: false,
        influence: a["influence"] ?? "medium", era: true,
      });
    }
  }
  return out;
}

function _f(v: any): number {
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : 0;
}

/** Standard Julian-Day → Gregorian {year, month, day} (matches swe.julday's Gregorian convention). */
export function jd_to_ymd(jd: number): { year: number; month: number; day: number } {
  const z = Math.floor(jd + 0.5);
  const f = jd + 0.5 - z;
  let a = z;
  if (z >= 2299161) {
    const alpha = Math.floor((z - 1867216.25) / 36524.25);
    a = z + 1 + alpha - Math.floor(alpha / 4);
  }
  const b = a + 1524;
  const c = Math.floor((b - 122.1) / 365.25);
  const d = Math.floor(365.25 * c);
  const e = Math.floor((b - d) / 30.6001);
  const day = Math.floor(b - d - Math.floor(30.6001 * e) + f);
  const month = e < 14 ? e - 1 : e - 13;
  const year = month > 2 ? c - 4716 : c - 4715;
  return { year, month, day };
}

/** "YYYY-MM" label for a Julian Day. */
function _jd_label(jd: number): string {
  const { year, month } = jd_to_ymd(jd);
  return `${year}-${String(month).padStart(2, "0")}`;
}

/** Enumerate the mahadasha periods (lord + start/end JD) from birth out to `horizon_jd`. */
function mahadasha_periods(
  birth_jd: number,
  start_index: number,
  first_dasha_end_jd: number,
  horizon_jd: number,
): Array<{ lord: string; start: number; end: number }> {
  const periods: Array<{ lord: string; start: number; end: number }> = [];
  let idx = pymod(start_index, 9);
  // The first mahadasha is PARTIAL — it began before birth; only its balance runs after birth.
  periods.push({ lord: _ORDER[idx], start: birth_jd, end: first_dasha_end_jd });
  let end = first_dasha_end_jd;
  let guard = 0;
  while (end < horizon_jd && guard < 60) {
    idx = pymod(idx + 1, 9);
    const lord = _ORDER[idx];
    const start = end;
    end = start + DASHA_DAYS[lord];
    periods.push({ lord, start, end });
    guard += 1;
  }
  return periods;
}

export interface EraSegment {
  /** "era" = all candidates agree on the running mahadasha; "transition" = candidates disagree (discriminating). */
  kind: "era" | "transition";
  start: string; // "YYYY-MM"
  end: string;
  /** era: the single agreed mahadasha lord. */
  lord?: string;
  /** transition: the competing lords (what life would be themed by, depending on the true birth time). */
  lords?: string[];
  /** width of the disagreement band, in years (larger = more discriminating). */
  span_years?: number;
}

/** Which mahadasha lord is running at `jd` for a given timeline, or null if outside it. */
function _lord_at(periods: Array<{ lord: string; start: number; end: number }>, jd: number): string | null {
  for (const p of periods) {
    if (jd >= p.start && jd < p.end) return p.lord;
  }
  return null;
}

/**
 * Build the chronological era/transition segments across the whole birth-time search range, by
 * scanning CALENDAR time (so it is robust even when the dasha SEQUENCE itself differs across the
 * range — e.g. a full-day window where the Moon crosses a nakshatra). Returns [] when birth/search
 * aren't usable. `now_jd` bounds output to already-lived eras. `min_transition_months` folds away
 * disagreement bands too narrow to be worth a question (there the boundary is already well-pinned).
 */
export function dasha_era_segments(
  user_profile: Record<string, any>,
  opts: { now_jd?: number; samples?: number; min_transition_months?: number } = {},
): { segments: EraSegment[]; complex: boolean } {
  const birth = user_profile["birth"] ?? {};
  const search = user_profile["search"] ?? {};
  if (!birth["date"] || !search["start"] || !search["end"]) return { segments: [], complex: false };
  const tz = _f(birth["timezone_offset"]);
  const lat = _f(birth["lat"]);
  const lon = _f(birth["lon"]);
  // birth_offset_at honours a user-confirmed fixed offset and pinned pre-1970 offsets — the SAME
  // birth-side offset resolution the scoring engine uses, so the era timelines line up with it.
  const birth_info = { lat, lon, timezone_offset: tz, timezone_offset_fixed: birth["timezone_offset_fixed"] };
  let bd: { year: number; month: number; day: number };
  try {
    bd = parseISODate(birth["date"]);
  } catch {
    return { segments: [], complex: false };
  }
  const [start_s, end_s] = _window_seconds(search);
  const samples = Math.max(2, Math.min(48, opts.samples ?? 16));

  // One mahadasha timeline per sampled candidate birth time across the WHOLE range.
  const horizon_years = 95;
  type TL = { periods: Array<{ lord: string; start: number; end: number }> };
  const timelines: TL[] = [];
  let birth_jd0 = 0;
  const midnight = plainDateTime(bd.year, bd.month, bd.day, 0, 0, 0);
  for (let i = 0; i < samples; i++) {
    // start_s..end_s can exceed 86400 for an overnight window; addSeconds rolls the date over
    // correctly (e.g. 01:00 in a 22:00→04:00 window lands on the NEXT calendar day, not the birth day).
    const sec = Math.round(start_s + ((end_s - start_s) * i) / (samples - 1));
    const dt = addSeconds(midnight, sec);
    let dinit: Record<string, number>;
    try {
      const tz_at = birth_offset_at(birth_info, dt, tz);
      const chart = calculate_chart(dt, lat, lon, tz_at, get_config());
      dinit = init_dasha_jd(chart, local_to_jd(dt, tz_at));
    } catch {
      continue;
    }
    birth_jd0 = dinit["birth_jd"];
    timelines.push({
      periods: mahadasha_periods(
        dinit["birth_jd"], dinit["start_index"], dinit["first_dasha_end_jd"],
        dinit["birth_jd"] + horizon_years * SIDEREAL_YEAR_DAYS,
      ),
    });
  }
  if (timelines.length < 2) return { segments: [], complex: false };

  const now_jd = opts.now_jd ?? birth_jd0 + horizon_years * SIDEREAL_YEAR_DAYS;
  const min_span_days = ((opts.min_transition_months ?? 2) / 12) * SIDEREAL_YEAR_DAYS;
  const month = SIDEREAL_YEAR_DAYS / 12;

  // Walk calendar time from birth to now in ~monthly steps. At each step collect the set of lords the
  // candidates assign; a single lord = consensus (era), several = disagreement (transition).
  type Raw = { lords: string[]; consensus: boolean; start: number; end: number };
  const raw: Raw[] = [];
  for (let jd = birth_jd0; jd <= now_jd; jd += month) {
    const lords = [...new Set(timelines.map((t) => _lord_at(t.periods, jd)).filter((l): l is string => !!l))].sort();
    if (!lords.length) continue;
    const consensus = lords.length === 1;
    const key = lords.join(",");
    const last = raw[raw.length - 1];
    if (last && last.consensus === consensus && last.lords.join(",") === key) {
      last.end = jd;
    } else {
      raw.push({ lords, consensus, start: jd, end: jd });
    }
  }

  // Fold away disagreement bands narrower than the threshold (boundary already well-pinned there),
  // then merge neighbouring consensus eras of the same lord across them.
  let complex = false;
  const segments: EraSegment[] = [];
  for (const r of raw) {
    if (!r.consensus) {
      if (r.lords.length > 2) complex = true;
      if (r.end - r.start < min_span_days) continue; // too narrow to ask about — skip
      segments.push({
        kind: "transition", lords: r.lords,
        start: _jd_label(r.start), end: _jd_label(r.end + month),
        span_years: Math.round(((r.end - r.start) / SIDEREAL_YEAR_DAYS) * 10) / 10,
      });
    } else {
      const prev = segments[segments.length - 1];
      if (prev && prev.kind === "era" && prev.lord === r.lords[0]) {
        prev.end = _jd_label(r.end + month);
      } else {
        segments.push({ kind: "era", lord: r.lords[0], start: _jd_label(r.start), end: _jd_label(r.end + month) });
      }
    }
  }
  return { segments, complex };
}
